/**
 * AddPlaceForm — manual create-place fallback. Used from /places list page
 * (sheet trigger) and /places/new direct route.
 *
 * Per AC-037 / UX-AC-036: required name + category; optional address +
 * status; on success route to /p/:placeId.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type { CategoryId, PlaceStatus, TagId } from '@shared/types';
import { Button } from './ui/Button';
import { TextInput } from './ui/TextInput';
import { FormField } from './ui/FormField';
import { useActiveHouseholdId } from '../lib/household';
import { useCategories, useCreatePlace, useTags } from '../lib/queries';
import { useToast } from '../lib/store';
import { useErrorToasts } from '../lib/errorToast';
import { cn } from '../lib/cn';

interface AddPlaceFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** When true, navigate to detail on success. Default true. */
  navigateOnSuccess?: boolean;
}

const STATUSES: PlaceStatus[] = ['untried', 'go-to', 'neutral', 'avoid'];

export function AddPlaceForm({
  onSuccess,
  onCancel,
  navigateOnSuccess = true,
}: AddPlaceFormProps) {
  const householdId = useActiveHouseholdId();
  const categories = useCategories(householdId);
  const tagsQ = useTags(householdId);
  const create = useCreatePlace(householdId);
  const toast = useToast();
  const errors = useErrorToasts();
  const navigate = useNavigate();

  const [name, setName] = React.useState('');
  const [categoryId, setCategoryId] = React.useState<CategoryId | ''>('');
  const [address, setAddress] = React.useState('');
  const [status, setStatus] = React.useState<PlaceStatus>('untried');
  const [tagIds, setTagIds] = React.useState<TagId[]>([]);
  const [touched, setTouched] = React.useState(false);

  const cats = categories.data?.categories ?? [];
  const tags = tagsQ.data?.tags ?? [];

  React.useEffect(() => {
    if (cats.length && !categoryId) {
      setCategoryId(cats[0]!.id);
    }
  }, [cats, categoryId]);

  const nameError = touched && !name.trim() ? 'Give the place a name.' : '';
  const categoryError =
    touched && !categoryId ? 'Choose a category.' : '';
  const formInvalid = Boolean(nameError) || Boolean(categoryError);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    if (!name.trim() || !categoryId) return;
    try {
      const place = await create.mutateAsync({
        name: name.trim(),
        category_id: categoryId,
        ...(address.trim() ? { address: address.trim() } : {}),
        status,
        ...(tagIds.length ? { tag_ids: tagIds } : {}),
      });
      toast.success('Place added.');
      onSuccess?.();
      if (navigateOnSuccess) {
        navigate(`/places/${place.id}`);
      }
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't add the place.",
        );
      }
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-6">
      {formInvalid && touched ? (
        <div
          className="px-4 py-3 border border-error-muted/40 bg-error-soft"
          role="alert"
        >
          <p className="font-serif italic text-sm text-error-muted leading-snug">
            A few details need attention before we can save this place.
          </p>
        </div>
      ) : null}

      <FormField label="Name" htmlFor="add-place-name" {...(nameError ? { error: nameError } : {})}>
        <TextInput
          id="add-place-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Augusta's"
          autoFocus
          required
        />
      </FormField>

      <FormField
        label="Category"
        htmlFor="add-place-category"
        {...(categoryError ? { error: categoryError } : {})}
      >
        <select
          id="add-place-category"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value as CategoryId)}
          className={cn(
            'block w-full bg-transparent border-0 border-b border-ink-300 px-0 py-3',
            'font-sans text-base text-ink-900',
            'focus:border-b-2 focus:border-accent focus:outline-none',
          )}
          required
        >
          <option value="">Pick a category</option>
          {cats.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </FormField>

      <FormField label="Address" optional htmlFor="add-place-address">
        <TextInput
          id="add-place-address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="10 Pine Ave"
        />
      </FormField>

      <FormField label="Status" htmlFor="add-place-status">
        <select
          id="add-place-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as PlaceStatus)}
          className={cn(
            'block w-full bg-transparent border-0 border-b border-ink-300 px-0 py-3',
            'font-sans text-base text-ink-900',
            'focus:border-b-2 focus:border-accent focus:outline-none',
          )}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </FormField>

      {tags.length ? (
        <FormField label="Tags" optional>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const active = tagIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() =>
                    setTagIds((prev) =>
                      active ? prev.filter((id) => id !== t.id) : [...prev, t.id],
                    )
                  }
                  aria-pressed={active}
                  className={cn(
                    'px-3 py-1 border text-xs lowercase rounded-full',
                    'transition-colors duration-200 ease-out-slow',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
                    active
                      ? 'bg-accent-soft border-accent text-accent'
                      : 'bg-transparent border-ink-200 text-ink-700 hover:bg-surface-2',
                  )}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </FormField>
      ) : null}

      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel ? (
          <Button variant="ghost" type="button" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button
          variant="primary"
          type="submit"
          loading={create.isPending}
          loadingLabel="Saving…"
          disabled={create.isPending}
        >
          Add place
        </Button>
      </div>
    </form>
  );
}
